import { CommunityTransactionForPurchaseQuery, propertyChangedJoinPointFilter } from '@skylab-shared/business';
import { BusinessObjectPart, BusinessObjectPartContext, CollectionChangingAction, ModelBase, ObservableCollection, PropertyChangedJoinPoint, Weaver } from '@skylab-shared/core';
import { DocumentPaymentListItemModel } from '@skylab-shared/dataModel';
import { dateDiffInDays } from '@skylab-shared/utils';

interface PurchaseModelPart extends ModelBase {
    vatDeductionDate: Date;
    receiptDate: Date;
    issueDate: Date;
    payments: ObservableCollection<DocumentPaymentListItemModel>,
    communityTransactionId: number;
    issuedByVatPayer: boolean;
    cashBasis: boolean;
}

export class PurchaseDocumentVatDeductionDateBOPart extends BusinessObjectPart<PurchaseModelPart> {
    public constructor(
        weaver: Weaver
    ) {
        super(weaver);
    }

    public override async initializeAsync(context: BusinessObjectPartContext<PurchaseModelPart>): Promise<void> {
        await super.initializeAsync(context);
        this.registerPropertyChangesRules();
    }

    private registerPropertyChangesRules(): void {
        let synchronizing = true;
        const { id: wntTransactionId } = this.bo.localContext.firstPreloaded(CommunityTransactionForPurchaseQuery, { symbol: 'WNT' });

        this.registerListening(this.bo.model.payments.onCollectionChanged(args => {
            if (!this.bo.model.issuedByVatPayer || (args.action !== CollectionChangingAction.Remove)) return;
            this.synchronizeWithPaymentDate();
        }));

        this.registerListening(this.weaver.register(PropertyChangedJoinPoint, propertyChangedJoinPointFilter(this.bo)(DocumentPaymentListItemModel, ['date'])(
            _ => {
                if (!this.bo.model.issuedByVatPayer) return;
                this.synchronizeWithPaymentDate();
            }
        )));

        this.registerListening(this.weaver.register(PropertyChangedJoinPoint, propertyChangedJoinPointFilter(this.bo)(this.modelType, ['vatDeductionDate', 'receiptDate', 'issueDate', 'communityTransactionId', 'cashBasis'])(
            ctx => {
                const model = this.bo.model;
                if (!this.bo.model.issuedByVatPayer) return;

                const dateToSynchronize = model.communityTransactionId === wntTransactionId ? model.issueDate : model.receiptDate;

                const result = this.synchronizeWithPaymentDate();
                if (result) return;

                if ((ctx.propertyName === 'vatDeductionDate') && (dateToSynchronize && model.vatDeductionDate && dateDiffInDays(model.vatDeductionDate, dateToSynchronize))) {
                    synchronizing = false;
                } else if (synchronizing || (!model.cashBasis && !model.vatDeductionDate)) {
                    model.vatDeductionDate = new Date(dateToSynchronize);
                }
            }
        )));
    }

    private synchronizeWithPaymentDate(): boolean {
        const model = this.bo.model;
        const { id: zkTransactionId } = this.bo.localContext.firstPreloaded(CommunityTransactionForPurchaseQuery, { symbol: 'ZK' });

        if ((model.communityTransactionId === zkTransactionId) && model.cashBasis) {
            const [firstPayment] = this.bo.model.payments.asArray().sort((p1, p2) => p1.date.getTime() - p2.date.getTime());
            model.vatDeductionDate = firstPayment?.date ?? null;
            return true;
        }

        return false;
    }
}
